import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

/**
 * Ticking a file reads it and puts it in the review queue.
 *
 * Selection used to be inert: it ticked a box, and nothing happened until the
 * operator typed a voucher number. Now the read starts on the tick and runs in
 * the background, so selecting a stack of thirty files leaves thirty answers
 * waiting by the time the operator gets to the Review tab.
 *
 * The click and the work are deliberately separated. What the operator clicked
 * is recorded and drawn immediately; a reconciler then drives the server toward
 * it. Anything else makes the checkbox lag a network round trip, and an operator
 * who does not see their click land clicks again - selecting and unselecting a
 * file several times without meaning to.
 *
 * That means three facts, not one:
 *   desired      what the operator last clicked  (instant, drives the checkbox)
 *   serverQueued what the queue actually holds   (what reconciliation aims at)
 *   statuses     what to draw                    (reading / in review / failed)
 *
 * Reads run three at a time. More would be faster in wall-clock terms and worse
 * in every other way: each one is a model call against a scanned PDF, and a
 * burst of thirty invites rate limits mid-stack, which reads to the operator as
 * random failures.
 */

const base = process.env.REACT_APP_API_BASE;
const queueRoute = process.env.REACT_APP_QUEUE_DOCUMENT || '/api/queuedocument';

async function postQueue(itemId, fileName, options = {}) {
  const response = await fetch(`${base}${queueRoute}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemId, fileName, ...options })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(data.error || data.details || `HTTP ${response.status}`);
  }
  return data;
}

async function deleteQueue(itemId) {
  const response = await fetch(`${base}${queueRoute}?id=${encodeURIComponent(itemId)}`, {
    method: 'DELETE'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(data.error || data.details || `HTTP ${response.status}`);
  }
  return data;
}

/**
 * How long a newly-seen file is left alone before it is read.
 *
 * A file appears in the OneDrive listing as soon as it is created, which can be
 * before the scanner has finished writing it. Reading a half-uploaded PDF fails
 * in a way that looks like a bad document rather than a race, so the file gets a
 * moment to settle first.
 */
const SETTLE_MS = 5000;

export function useReviewQueueing({ concurrency = 3, onChange, settleMs = SETTLE_MS } = {}) {
  // fileId -> { state, documentId, segmentCount, band, error } where state is
  //   waiting  settling after upload, or queued behind the concurrency limit
  //   reading  a model call is in flight right now
  //   queued   read, and sitting in the review queue
  //   filed    already archived, nothing to review
  //   rejected a human said this is not an invoice; never read again
  //   error    the read failed; waits for an explicit retry
  const [statuses, setStatuses] = useState({});
  // Exposed so the caller can wait for it before auto-reading. A ref alone
  // would not re-run their effect once the queue finally arrives.
  const [hydrated, setHydrated] = useState(false);

  const desired = useRef(new Map());      // fileId -> boolean, the last click
  const serverQueued = useRef(new Map()); // fileId -> documentId, what the queue holds
  const busy = useRef(new Set());         // a request is in flight for this file
  const failed = useRef(new Set());       // do not retry until asked
  const reread_ = useRef(new Set());      // read again even though it is queued
  const manual_ = useRef(new Set());      // open an empty form instead of reading
  const settling = useRef(new Map());     // fileId -> timer, waiting to be read
  const hydratedOnce = useRef(false);     // has the server told us what it holds?
  const unmounted = useRef(false);
  const changed = useRef(onChange);

  const pending = useRef([]);
  const active = useRef(0);

  useEffect(() => {
    changed.current = onChange;
  }, [onChange]);

  useEffect(() => {
    // Reset on setup, not just on teardown. StrictMode runs mount -> cleanup ->
    // mount in development, and a flag that is only ever set to true leaves
    // every later status update silently dropped: the reads still happen, but
    // nothing they report ever reaches the screen.
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      for (const timer of settling.current.values()) clearTimeout(timer);
      settling.current.clear();
    };
  }, []);

  const setStatus = useCallback((fileId, value) => {
    if (unmounted.current) return;
    setStatuses((prev) => {
      if (value === null) {
        if (!prev[fileId]) return prev;
        const next = { ...prev };
        delete next[fileId];
        return next;
      }
      return { ...prev, [fileId]: { ...(prev[fileId] || {}), ...value } };
    });
  }, []);

  /* ------------------------------------------------ concurrency-limited reads */

  const pump = useCallback(() => {
    while (active.current < concurrency && pending.current.length > 0) {
      const job = pending.current.shift();
      active.current += 1;
      job().finally(() => {
        active.current -= 1;
        pump();
      });
    }
  }, [concurrency]);

  // Only the read is limited. Removing a file is cheap and should never sit
  // behind three PDFs being read.
  const limited = useCallback(
    (task) =>
      new Promise((resolve, reject) => {
        pending.current.push(() => task().then(resolve, reject));
        pump();
      }),
    [pump]
  );

  /* ----------------------------------------------------------- reconciliation */

  const reconcileRef = useRef(null);

  const reconcile = useCallback(
    async (file, { force = false } = {}) => {
      const fileId = file?.id;
      if (!fileId) return;
      if (busy.current.has(fileId)) return;      // the finally block will re-check
      if (!force && failed.current.has(fileId)) return;

      const wanted = desired.current.get(fileId) === true;
      const queued = serverQueued.current.has(fileId);
      const reread = reread_.current.has(fileId);
      const manual = manual_.current.has(fileId);

      if (wanted === queued && !reread && !manual) {
        // Settled. Make what is drawn agree with what is true.
        if (!wanted && !failed.current.has(fileId)) setStatus(fileId, null);
        return;
      }

      busy.current.add(fileId);
      try {
        if (wanted) {
          const data = await limited(() => {
            // Only now is it genuinely being read - up to this point it was
            // sitting behind the concurrency limit, which on a full backlog can
            // be several minutes. Saying "Reading" for that whole time would be
            // a lie the operator would reasonably respond to by pressing again.
            setStatus(fileId, { state: manual ? 'waiting' : 'reading', error: null });
            return postQueue(fileId, file.name, { force: reread, manual });
          });
          failed.current.delete(fileId);
          reread_.current.delete(fileId);
          manual_.current.delete(fileId);

          // The operator may have unticked while this was in flight. Record what
          // the server now holds, but do not repaint a box they have moved on
          // from - the reconcile below will take it back out.
          const stillWanted = desired.current.get(fileId) === true;

          if (data.alreadyFiled || data.alreadyRejected) {
            // A human already ruled on this one. Record the outcome so it is
            // never picked up for reading again this session.
            desired.current.set(fileId, false);
            serverQueued.current.delete(fileId);
            setStatus(fileId, {
              state: data.alreadyRejected ? 'rejected' : 'filed',
              documentId: data.documentId,
              filedAs: data.filedAs || null
            });
          } else {
            serverQueued.current.set(fileId, data.documentId);
            if (stillWanted) {
              setStatus(fileId, {
                state: 'queued',
                documentId: data.documentId,
                segmentCount: data.segmentCount || 0,
                band: data.proposals?.[0]?.band || 'none',
                proposals: data.proposals || [],
                error: null
              });
            }
          }
        } else {
          await deleteQueue(fileId);
          failed.current.delete(fileId);
          serverQueued.current.delete(fileId);
          if (desired.current.get(fileId) !== true) setStatus(fileId, null);
        }
        changed.current?.();
      } catch (err) {
        const message = err?.message || String(err);
        failed.current.add(fileId);

        if (wanted) {
          setStatus(fileId, { state: 'error', error: message });
        } else {
          // It could not be removed, so it is still queued. Put the tick back
          // rather than show a cleared box over a file that is still in review.
          desired.current.set(fileId, true);
          setStatus(fileId, { state: 'queued', error: message });
        }
      } finally {
        busy.current.delete(fileId);
        reconcileRef.current?.(file); // the intent may have changed while we were away
      }
    },
    [limited, setStatus]
  );

  useEffect(() => {
    reconcileRef.current = reconcile;
  }, [reconcile]);

  /* ------------------------------------------------------------------ the API */

  /**
   * Record a click. Returns as soon as the checkbox has been updated; the
   * request it causes happens afterwards and takes as long as it takes.
   */
  const setSelected = useCallback(
    (file, wanted) => {
      const fileId = file?.id;
      if (!fileId) return;

      desired.current.set(fileId, wanted);
      failed.current.delete(fileId); // a fresh click is a fresh intent

      if (wanted) {
        setStatuses((prev) =>
          prev[fileId]?.state === 'queued'
            ? prev
            : { ...prev, [fileId]: { ...(prev[fileId] || {}), state: 'waiting', error: null } }
        );
      } else {
        setStatus(fileId, null);
      }

      reconcile(file);
    },
    [reconcile, setStatus]
  );

  const toggle = useCallback(
    (file) => setSelected(file, desired.current.get(file?.id) !== true),
    [setSelected]
  );

  /**
   * Read this file again from scratch.
   *
   * Not the same as queueing it: a queued file is deliberately never re-read,
   * which would otherwise make a bad read permanent. Retry discards what we
   * have and asks the model to look again.
   */
  const retry = useCallback(
    (file) => {
      if (!file?.id) return;
      const timer = settling.current.get(file.id);
      if (timer) {
        clearTimeout(timer);
        settling.current.delete(file.id);
      }
      failed.current.delete(file.id);
      reread_.current.add(file.id);
      desired.current.set(file.id, true);
      // Land the feedback on the click, not on the response.
      setStatus(file.id, { state: 'waiting', error: null });
      reconcile(file, { force: true });
    },
    [reconcile, setStatus]
  );

  /**
   * Read anything that has not been looked at yet.
   *
   * Called with the whole file list, so a scan that lands in OneDrive is read
   * without anyone asking. Already-queued, already-filed and currently-failed
   * files are skipped, so this is safe to call on every refresh - it converges
   * rather than re-spending.
   */
  /**
   * Put a file into review without reading it.
   *
   * The override for a scan the reader cannot handle - a smudged voucher, an
   * unusual layout, anything where the answer will be typed rather than
   * corrected. It costs no model call and lands in the same review card, with
   * the scan on one side and an empty form on the other.
   */
  const indexManually = useCallback(
    (file) => {
      if (!file?.id) return;
      manual_.current.add(file.id);
      failed.current.delete(file.id);
      desired.current.set(file.id, true);
      setStatus(file.id, { state: 'waiting', error: null });
      reconcile(file, { force: true });
    },
    [reconcile, setStatus]
  );

  const autoRead = useCallback(
    (files) => {
      // Until the server has told us what is already in the queue, every file
      // looks unread. Acting on that would fire a request per file on every
      // login to be told "already queued" - noise at best, and it makes the UI
      // claim work is happening that is not.
      if (!hydratedOnce.current) return;

      for (const file of files || []) {
        const fileId = file?.id;
        if (!fileId) continue;
        if (desired.current.has(fileId)) continue;   // already decided about it
        if (failed.current.has(fileId)) continue;    // wait for an explicit retry
        if (settling.current.has(fileId)) continue;  // already counting down

        setStatus(fileId, { state: 'waiting' });

        const timer = setTimeout(() => {
          settling.current.delete(fileId);
          if (unmounted.current) return;
          if (desired.current.has(fileId)) return;   // decided while we waited
          setSelected(file, true);
        }, settleMs);

        settling.current.set(fileId, timer);
      }
    },
    [setSelected, setStatus, settleMs]
  );

  /**
   * Reconcile with what the server actually holds.
   *
   * The queue outlives the page. Without this, a reload shows an empty
   * "Selected (0)" next to a Review tab badge of 12 - the two numbers the whole
   * feature exists to keep equal. Clicks the server has not caught up with yet
   * win: a box the operator just ticked must not untick itself because a
   * refresh landed first.
   */
  const hydrate = useCallback((documents) => {
    hydratedOnce.current = true;
    setHydrated(true);
    const live = new Map();
    for (const doc of documents || []) {
      if (doc?.driveItemId) live.set(doc.driveItemId, doc);
    }
    serverQueued.current = new Map([...live].map(([id, doc]) => [id, doc.id]));

    setStatuses((prev) => {
      const next = {};

      for (const [fileId, entry] of Object.entries(prev)) {
        const wanted = desired.current.get(fileId);
        const inFlight = busy.current.has(fileId);
        if (
          inFlight ||
          wanted === true ||
          entry.state === 'waiting' ||
          entry.state === 'error' ||
          entry.state === 'filed'
        ) {
          next[fileId] = entry;
        }
      }

      for (const [fileId, doc] of live) {
        if (desired.current.get(fileId) === false) continue; // just unticked
        const pending = next[fileId]?.state;
        if (pending === 'reading' || pending === 'waiting') continue; // mid-flight
        const proposals = doc.proposals || [];
        next[fileId] = {
          state: 'queued',
          documentId: doc.id,
          segmentCount: proposals.length,
          // Slips are counted separately so the row can say what it really is.
          // "In review - 1 invoice" on a packing slip would send the clerk to
          // the wrong tab looking for it.
          slipCount: proposals.filter((p) => p?.documentType === 'packing_slip').length,
          band: proposals[0]?.band || 'none',
          proposals
        };
        if (!desired.current.has(fileId)) desired.current.set(fileId, true);
      }

      return next;
    });
  }, []);

  const counts = useMemo(() => {
    const out = { waiting: 0, reading: 0, queued: 0, error: 0, filed: 0, rejected: 0, total: 0 };
    for (const entry of Object.values(statuses)) {
      out.total += 1;
      if (entry.state === 'waiting') out.waiting += 1;
      else if (entry.state === 'reading') out.reading += 1;
      else if (entry.state === 'queued') out.queued += 1;
      else if (entry.state === 'error') out.error += 1;
      else if (entry.state === 'filed') out.filed += 1;
      else if (entry.state === 'rejected') out.rejected += 1;
    }
    return out;
  }, [statuses]);

  const get = useCallback((fileId) => statuses[fileId] || null, [statuses]);

  const reset = useCallback(() => {
    pending.current = [];
    busy.current = new Set();
    failed.current = new Set();
    reread_.current = new Set();
    manual_.current = new Set();
    for (const timer of settling.current.values()) clearTimeout(timer);
    settling.current.clear();
    desired.current = new Map();
    serverQueued.current = new Map();
    setStatuses({});
  }, []);

  return {
    setSelected,
    toggle,
    retry,
    indexManually,
    autoRead,
    hydrate,
    hydrated,
    statuses,
    counts,
    get,
    reset
  };
}

export default useReviewQueueing;
